import { useState, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import Topbar from "@/components/Topbar";
import PasswordInput from "@/components/PasswordInput";
import { LockKey, ArrowRight, CheckCircle, XCircle } from "@phosphor-icons/react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  // Some email clients (Outlook safe-links, Apple Mail) URL-decode tokens or
  // wrap them with stray whitespace.  `.trim()` defends against the common
  // newline-suffix variant that yields "invalid or expired" on submit.
  const token = useMemo(() => (params.get("token") || "").trim(), [params]);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const nav = useNavigate();

  const tooShort = pw && pw.length < 10;
  const mismatch = pw && pw2 && pw !== pw2;
  const canSubmit = !!token && pw.length >= 8 && pw === pw2 && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => nav("/login"), 2500);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(formatApiErrorDetail(detail) || "Could not reset password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="industrial min-h-screen bg-[#0A0C10] flex flex-col">
      <Topbar />
      <div className="flex-1 flex items-center justify-center p-8 md:p-12">
        <div className="w-full max-w-md" data-testid="reset-password-page">
          <div className="label-tag">// Set new password</div>
          <h1 className="font-display text-3xl font-black mt-2">Choose a new password</h1>

          {!token && (
            <div className="mt-6 p-4 border border-red-500/40 bg-red-500/10 text-red-300 text-sm font-mono" data-testid="reset-no-token">
              <XCircle weight="fill" size={16} className="inline mr-1" />
              Missing reset token. Use the link from your email or
              <Link to="/forgot-password" className="text-[#00E5FF] hover:underline ml-1">request a new one</Link>.
            </div>
          )}

          {token && done && (
            <div className="mt-6 p-5 border border-[#00E5FF]/40 bg-[#00E5FF]/5 font-mono text-sm space-y-2" data-testid="reset-password-success">
              <div className="inline-flex items-center gap-2 text-[#00E5FF] font-bold">
                <CheckCircle size={16} weight="fill" /> Password updated
              </div>
              <p className="text-gray-300 text-xs">Redirecting you to sign in…</p>
            </div>
          )}

          {token && !done && (
            <form onSubmit={submit} className="mt-6" data-testid="reset-password-form">
              {error && (
                <div className="mb-4 p-3 border border-red-500/40 bg-red-500/10 text-red-300 text-sm font-mono" data-testid="reset-password-error">
                  {error}
                </div>
              )}
              <label className="block">
                <span className="label-tag">New password (10+ chars, letter + digit + special)</span>
                <div className="mt-2">
                  <PasswordInput
                    required
                    minLength={10}
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    autoComplete="new-password"
                    placeholder="At least 10 characters"
                    data-testid="reset-password-new"
                    showStrength
                  />
                </div>
              </label>
              {tooShort && (
                <div className="mt-1 text-xs text-yellow-400 font-mono" data-testid="reset-password-short">
                  Minimum 10 characters.
                </div>
              )}

              <label className="block mt-4">
                <span className="label-tag">Confirm password</span>
                <div className="mt-2">
                  <PasswordInput
                    required
                    value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                    autoComplete="new-password"
                    placeholder="Repeat the new password"
                    data-testid="reset-password-confirm"
                  />
                </div>
              </label>
              {mismatch && (
                <div className="mt-1 text-xs text-red-400 font-mono" data-testid="reset-password-mismatch">
                  Passwords don&apos;t match.
                </div>
              )}

              <button
                type="submit"
                disabled={!canSubmit}
                className="btn-primary w-full mt-6 inline-flex items-center justify-center gap-2"
                data-testid="reset-password-submit"
              >
                {busy ? "Saving…" : (<><LockKey size={18} weight="bold" /> Update password <ArrowRight size={16} /></>)}
              </button>
              <p className="mt-6 text-sm text-gray-400">
                <Link to="/login" className="text-[#00E5FF] hover:underline">Back to sign in</Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
